/**
 * Unified File Handling Utility
 * 
 * Provides a consistent way to handle file uploads across the entire application.
 * Automatically decides between base64 inline data and GCS upload based on file size.
 */

// File size thresholds (must match backend thresholds in function_executor.py)
const FILE_SIZE_THRESHOLDS = {
  video: 5 * 1024 * 1024,    // 5MB - videos are large, use GCS early
  audio: 3 * 1024 * 1024,    // 3MB - audio transcription benefits from GCS
  mp3: 3 * 1024 * 1024,      // 3MB
  image: 10 * 1024 * 1024,   // 10MB - balance latency vs size
  pdf: 10 * 1024 * 1024,     // 10MB
  file: 10 * 1024 * 1024,    // 10MB - default
};

export interface FileMetadata {
  filename: string;
  file_size: number;
  content_type: string;
  gcs_url?: string;           // GCS URL if file was uploaded to cloud storage
  public_url?: string;         // Public HTTPS URL if file was uploaded to cloud storage
  uploaded_to_gcs?: boolean;   // Flag indicating if file was uploaded to GCS
}

export interface FileHandlerResult {
  // What to send to the API
  input: string;               // Base64 data URL OR text content OR GCS URL
  input_type: string;          // 'text', 'image', 'video', 'audio', 'pdf', 'file'
  metadata: FileMetadata;
  
  // Optional fields
  message?: string;            // Additional instructions/context
}

/**
 * Detects file type from MIME type and filename
 */
export function detectFileType(contentType: string, filename?: string): string {
  const type = contentType.toLowerCase();
  const fname = filename?.toLowerCase() || '';
  
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'application/pdf' || fname.endsWith('.pdf')) return 'pdf';
  if (type === 'application/json' || fname.endsWith('.json')) return 'json';
  if (type.startsWith('text/')) return 'text';
  
  return 'file';
}

/**
 * Determines if a file should use GCS upload based on size and type
 */
export function shouldUseGCS(fileSize: number, fileType: string): boolean {
  const threshold = FILE_SIZE_THRESHOLDS[fileType as keyof typeof FILE_SIZE_THRESHOLDS] 
    || FILE_SIZE_THRESHOLDS.file;
  
  return fileSize > threshold;
}

/**
 * Reads a file as base64 data URL
 */
export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Reads a file as text
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

/**
 * Upload a file to the temp GCS bucket (24-hour auto-cleanup)
 * @param file - File to upload
 * @param authToken - Firebase auth token
 * @returns Upload response with gcs_url and public_url
 */
async function uploadToTempBucket(file: File, authToken: string): Promise<{
  gcs_url: string;
  public_url: string;
  file_size: number;
  filename: string;
}> {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch('/api/v2/temp/upload', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
    body: formData,
  });
  
  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Main function: Prepare a file for API submission
 * 
 * This is the ONE method that should be used everywhere for file handling.
 * It automatically handles:
 * - File type detection
 * - Size checking
 * - Base64 encoding for small files (fast)
 * - GCS upload for large files (efficient, uses /api/v2/temp/upload)
 * - Metadata preparation
 * 
 * @param file - The File object to process
 * @param additionalMessage - Optional text message to accompany the file
 * @param authToken - Optional Firebase auth token (required for GCS upload of large files)
 * @returns FileHandlerResult ready to be sent to the API
 */
export async function prepareFileForSubmission(
  file: File,
  additionalMessage?: string,
  authToken?: string
): Promise<FileHandlerResult> {
  // Detect file type
  const fileType = detectFileType(file.type, file.name);
  
  // Prepare metadata
  const metadata: FileMetadata = {
    filename: file.name,
    file_size: file.size,
    content_type: file.type || 'application/octet-stream',
  };
  
  // Check if we should use GCS upload
  const useGCS = shouldUseGCS(file.size, fileType);
  
  let input: string = '';  // Initialize to empty string for type safety
  
  if (useGCS && authToken) {
    // Upload to GCS for large files (efficient!)
    console.log(`File ${file.name} is large (${(file.size / 1024 / 1024).toFixed(2)}MB), uploading to GCS...`);
    
    try {
      const uploadResult = await uploadToTempBucket(file, authToken);
      
      // For GCS uploads, we send the gcs_url as input
      // The backend will use this instead of base64
      input = uploadResult.gcs_url;
      
      // Add GCS metadata
      (metadata as any).gcs_url = uploadResult.gcs_url;
      (metadata as any).public_url = uploadResult.public_url;
      (metadata as any).uploaded_to_gcs = true;
      
      console.log(`✅ Uploaded to GCS: ${uploadResult.gcs_url.substring(0, 60)}...`);
    } catch (error) {
      console.warn(`GCS upload failed, falling back to base64:`, error);
      // Fall back to base64 if upload fails
      input = await readFileAsDataURL(file);
    }
  } else {
    // Use base64 for small files (fast!)
    if (fileType === 'text' || fileType === 'json') {
      // Read as text for text files
      input = await readFileAsText(file);
    } else {
      // Read as base64 data URL for binary files
      input = await readFileAsDataURL(file);
    }
  }
  
  // Ensure input is never undefined
  if (!input) {
    throw new Error('Failed to process file - no input data generated');
  }
  
  const result: FileHandlerResult = {
    input,
    input_type: fileType,
    metadata,
  };
  
  // Add message if provided
  if (additionalMessage) {
    result.message = additionalMessage;
  }
  
  return result;
}

/**
 * Convenience function: Prepare a text input for API submission
 */
export function prepareTextForSubmission(
  text: string,
  inputType: 'text' | 'json' = 'text'
): FileHandlerResult {
  return {
    input: text,
    input_type: inputType,
    metadata: {
      filename: 'text-input',
      file_size: new Blob([text]).size,
      content_type: inputType === 'json' ? 'application/json' : 'text/plain',
    },
  };
}

/**
 * Convenience function: Prepare a base64 data URL (from test materials, etc.)
 */
export function prepareDataURLForSubmission(
  dataURL: string,
  filename?: string,
  additionalMessage?: string
): FileHandlerResult {
  // Extract metadata from data URL
  const match = dataURL.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) {
    throw new Error('Invalid data URL format');
  }
  
  const contentType = match[1];
  const base64Data = match[2];
  const fileSize = Math.floor((base64Data.length * 3) / 4); // Approximate decoded size
  
  const fileType = detectFileType(contentType, filename);
  
  const result: FileHandlerResult = {
    input: dataURL,
    input_type: fileType,
    metadata: {
      filename: filename || 'uploaded-file',
      file_size: fileSize,
      content_type: contentType,
    },
  };
  
  if (additionalMessage) {
    result.message = additionalMessage;
  }
  
  return result;
}

/**
 * Build the complete request body for the API
 * This creates the exact JSON structure expected by the backend
 */
export function buildAPIRequestBody(
  fileHandlerResult: FileHandlerResult,
  model?: string,
  additionalMetadata?: Record<string, any>
): Record<string, any> {
  const requestBody: Record<string, any> = {
    input: fileHandlerResult.input,
    input_type: fileHandlerResult.input_type,
    metadata: {
      ...fileHandlerResult.metadata,
      ...additionalMetadata,
    },
  };
  
  if (model) {
    requestBody.model = model;
  }
  
  if (fileHandlerResult.message) {
    requestBody.message = fileHandlerResult.message;
  }
  
  return requestBody;
}

/**
 * Example usage:
 * 
 * // For file uploads:
 * const file = event.target.files[0];
 * const prepared = await prepareFileForSubmission(file, "Analyze this video");
 * const requestBody = buildAPIRequestBody(prepared, "whisper-large-v3");
 * 
 * // For text input:
 * const prepared = prepareTextForSubmission("Hello world", "text");
 * const requestBody = buildAPIRequestBody(prepared, "gpt-4o-mini");
 * 
 * // For test materials with base64:
 * const prepared = prepareDataURLForSubmission(dataURL, "test.mp4", "Transcribe this");
 * const requestBody = buildAPIRequestBody(prepared, "whisper-large-v3");
 * 
 * // Then send to API:
 * fetch(`/api/v2/functions/${customerTag}/${functionName}`, {
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json', ... },
 *   body: JSON.stringify(requestBody)
 * });
 */
